import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Loader2, Check, X, AlertTriangle, Ban, Edit3 } from 'lucide-react'
import { matchService, formatScore, type ScoreSet, type DisputeDesiredOutcome } from '@/services/matchService'
import { BadmintonScoreEntry } from './BadmintonSetScore'

type MatchFormat = 'bestOf1' | 'bestOf3' | 'bestOf5'

interface PlayerInfo {
  userId: string
  displayName: string
  avatar?: string
}

interface ScoreConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'accept' | 'dispute'
  // Match details from notification
  matchId: string
  bracketId: string
  submitterName?: string
  score?: string
  sets?: ScoreSet[]
  matchFormat?: MatchFormat
  player1?: PlayerInfo
  player2?: PlayerInfo
  // Callbacks
  onSuccess: () => void
  onError?: (error: string) => void
}

// Get max sets allowed based on match format
function getMaxSets(format: MatchFormat): number {
  switch (format) {
    case 'bestOf1': return 1
    case 'bestOf3': return 3
    case 'bestOf5': return 5
    default: return 3
  }
}

// Get initials for avatar fallback
function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function ScoreConfirmDialog({
  open,
  onOpenChange,
  mode,
  matchId,
  bracketId,
  submitterName = 'Your opponent',
  score,
  sets,
  matchFormat = 'bestOf3',
  player1,
  player2,
  onSuccess,
  onError,
}: ScoreConfirmDialogProps) {
  const [loading, setLoading] = useState(false)
  const [disputeReason, setDisputeReason] = useState('')
  const [desiredOutcome, setDesiredOutcome] = useState<DisputeDesiredOutcome>('new_score')
  const [proposedSets, setProposedSets] = useState<ScoreSet[]>([{ player1Score: 0, player2Score: 0 }])
  const [error, setError] = useState<string | null>(null)

  const maxSets = getMaxSets(matchFormat)
  const scoreDisplay = score || (sets ? formatScore(sets) : 'Unknown score')

  // Pre-fill proposed score with existing score when dialog opens in dispute mode
  useEffect(() => {
    if (open && mode === 'dispute' && sets && sets.length > 0) {
      setProposedSets([...sets])
    } else if (open && mode === 'dispute') {
      setProposedSets([{ player1Score: 0, player2Score: 0 }])
    }
  }, [open, mode, sets])

  const handleConfirm = async () => {
    setLoading(true)
    setError(null)

    try {
      if (mode === 'accept') {
        await matchService.approve(bracketId, matchId)
      } else {
        await matchService.dispute(bracketId, matchId, {
          reason: disputeReason.trim() || undefined,
          desiredOutcome,
          proposedSets: desiredOutcome === 'new_score' ? proposedSets : undefined,
        })
      }
      onSuccess()
      onOpenChange(false)
      resetForm()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred'
      setError(message)
      onError?.(message)
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setDisputeReason('')
    setDesiredOutcome('new_score')
    setProposedSets([{ player1Score: 0, player2Score: 0 }])
    setError(null)
  }

  const handleClose = () => {
    if (!loading) {
      onOpenChange(false)
      resetForm()
    }
  }


  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'accept' ? (
              <>
                <Check className="h-5 w-5 text-green-600" />
                Confirm Match Score
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                Dispute Match Score
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {mode === 'accept'
              ? 'Confirm that this score is correct. This will update the standings.'
              : 'Dispute this score if you believe it is incorrect. Choose what you want to happen.'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Score display with player info */}
          <div className="rounded-lg bg-muted/50 p-4">
            <p className="text-xs text-muted-foreground text-center mb-3">Submitted by {submitterName}</p>
            <div className="flex items-center justify-center gap-4">
              {/* Player 1 */}
              <div className="flex flex-col items-center gap-1.5 min-w-[80px]">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={player1?.avatar} alt={player1?.displayName} />
                  <AvatarFallback className="text-xs">
                    {player1 ? getInitials(player1.displayName) : 'P1'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs font-medium text-center line-clamp-1">
                  {player1?.displayName || 'Player 1'}
                </span>
              </div>

              {/* Score */}
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold">{scoreDisplay}</span>
              </div>

              {/* Player 2 */}
              <div className="flex flex-col items-center gap-1.5 min-w-[80px]">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={player2?.avatar} alt={player2?.displayName} />
                  <AvatarFallback className="text-xs">
                    {player2 ? getInitials(player2.displayName) : 'P2'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs font-medium text-center line-clamp-1">
                  {player2?.displayName || 'Player 2'}
                </span>
              </div>
            </div>
          </div>

          {/* Dispute options */}
          {mode === 'dispute' && (
            <>
              {/* Desired outcome selection */}
              <div className="space-y-2">
                <Label>What do you want to happen?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setDesiredOutcome('new_score')}
                    className={`flex flex-col items-center gap-1 rounded-lg border-2 p-3 transition-colors ${
                      desiredOutcome === 'new_score'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <Edit3 className="h-5 w-5" />
                    <span className="text-sm font-medium">Propose New Score</span>
                    <span className="text-xs text-muted-foreground">Enter the correct score</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDesiredOutcome('invalidate')}
                    className={`flex flex-col items-center gap-1 rounded-lg border-2 p-3 transition-colors ${
                      desiredOutcome === 'invalidate'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <Ban className="h-5 w-5" />
                    <span className="text-sm font-medium">Invalidate Match</span>
                    <span className="text-xs text-muted-foreground">Delete this match</span>
                  </button>
                </div>
              </div>

              {/* Proposed score input (only for new_score) */}
              {desiredOutcome === 'new_score' && (
                <div className="space-y-3 rounded-lg border p-3">
                  <Label>What was the actual score?</Label>
                  <BadmintonScoreEntry
                    player1={{ displayName: player1?.displayName || 'Player 1', avatar: player1?.avatar }}
                    player2={{ displayName: player2?.displayName || 'Player 2', avatar: player2?.avatar }}
                    sets={proposedSets}
                    onSetsChange={setProposedSets}
                    minSets={1}
                    maxSets={maxSets}
                    setsToWin={Math.ceil(maxSets / 2)}
                  />
                  <p className="text-xs text-muted-foreground text-center">
                    Tap a player to select the winner
                  </p>
                </div>
              )}

              {/* Reason input */}
              <div className="space-y-2">
                <Label htmlFor="dispute-reason">Reason for dispute (optional)</Label>
                <Textarea
                  id="dispute-reason"
                  placeholder="Explain why this score is incorrect..."
                  value={disputeReason}
                  onChange={(e) => setDisputeReason(e.target.value)}
                  rows={2}
                  maxLength={500}
                />
              </div>
            </>
          )}

          {/* Error display */}
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 flex items-center gap-2 text-sm text-destructive">
              <X className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading}
            variant={mode === 'accept' ? 'default' : 'destructive'}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {mode === 'accept' ? 'Confirming...' : 'Disputing...'}
              </>
            ) : mode === 'accept' ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Confirm Score
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 mr-2" />
                Submit Dispute
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
